import React, { useCallback, useEffect, useState } from 'react'
import { PORTRAIT } from '../../utils/orientation'

/**
 * What the device actually reports, rather than what we assume.
 *
 * Three attempts at locking rotation have failed, all of them variations on
 * calling screen.orientation.lock(). That call is refused outside a fullscreen
 * or installed context and the rejection is invisible, so guessing at the call
 * site cannot make progress. This panel reads the answers straight off the
 * device instead: whether the browser considers the app installed, what the
 * orientation API says, and precisely how a lock attempt fails.
 *
 * Reached by tapping the "Prism Handheld" title three times, so it costs the
 * operator nothing and cannot be opened by accident.
 */
export default function PdaDiagnostics({ onClose }) {
    const [facts, setFacts] = useState(() => gather())
    const [attempt, setAttempt] = useState('not tried')
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        // Orientation can change while the panel is open, which is the whole
        // complaint; keep the reading honest.
        const refresh = () => setFacts(gather())
        window.addEventListener('resize', refresh)
        screen.orientation?.addEventListener?.('change', refresh)
        return () => {
            window.removeEventListener('resize', refresh)
            screen.orientation?.removeEventListener?.('change', refresh)
        }
    }, [])

    // Runs inside a real tap, which is the only context where fullscreen and
    // therefore locking can be granted in a browser tab.
    const tryLock = useCallback(async () => {
        const steps = []
        try {
            await document.documentElement.requestFullscreen?.({ navigationUI: 'hide' })
            steps.push('fullscreen: granted')
        } catch (error) {
            steps.push(`fullscreen: ${error.name} ${error.message}`)
        }
        try {
            await screen.orientation.lock(PORTRAIT)
            steps.push('lock: granted')
        } catch (error) {
            steps.push(`lock: ${error.name} ${error.message}`)
        }
        setAttempt(steps.join(' | '))
        setFacts(gather())
    }, [])

    const report = [...Object.entries(facts), ['lock attempt', attempt]]
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n')

    const copy = useCallback(() => {
        navigator.clipboard?.writeText(report).then(
            () => setCopied(true),
            () => setCopied(false)
        )
    }, [report])

    return (
        <div className="pda-diag">
            <div className="pda-diag-head">
                <strong>Diagnostics</strong>
                <button className="pda-btn" onClick={onClose} style={{ width: 'auto', margin: 0, padding: '8px 14px' }}>
                    Close
                </button>
            </div>

            <dl className="pda-diag-list">
                {Object.entries(facts).map(([key, value]) => (
                    <React.Fragment key={key}>
                        <dt>{key}</dt>
                        <dd>{String(value)}</dd>
                    </React.Fragment>
                ))}
                <dt>lock attempt</dt>
                <dd>{attempt}</dd>
            </dl>

            <button className="pda-btn" onClick={tryLock}>Try to lock rotation now</button>
            <button className="pda-btn" onClick={copy}>{copied ? 'Copied' : 'Copy this report'}</button>
        </div>
    )
}

function gather() {
    return {
        'display mode': displayMode(),
        'orientation': screen.orientation?.type ?? 'no orientation API',
        'orientation angle': screen.orientation?.angle ?? 'n/a',
        'lock supported': typeof screen.orientation?.lock === 'function',
        'in fullscreen': Boolean(document.fullscreenElement),
        'manifest linked': Boolean(document.querySelector('link[rel="manifest"]')),
        'viewport': `${window.innerWidth} x ${window.innerHeight}`,
        'screen': `${window.screen?.width} x ${window.screen?.height}`,
        'pixel ratio': window.devicePixelRatio,
        'touch points': navigator.maxTouchPoints,
        'user agent': navigator.userAgent
    }
}

/**
 * How the browser thinks the app was launched. A home-screen icon that reports
 * `browser` was saved as a plain bookmark rather than installed, which would
 * explain everything: a bookmark gets none of the manifest, so it can neither
 * hide the address bar nor hold an orientation.
 */
function displayMode() {
    for (const mode of ['standalone', 'fullscreen', 'minimal-ui']) {
        if (window.matchMedia(`(display-mode: ${mode})`).matches) return mode
    }
    return 'browser'
}
